import { memo } from 'react';
import { Camera, ChevronRight } from 'lucide-react';
import { t } from '@lingui/core/macro';
import { canAddMorePhotos, effectiveMaxPhotos, isStepRequiredDone } from '@transpordiamet/shared';
import Header from '@/components/header';
import { CaptureActionRail, CaptureShutterButton } from '@/components/CaptureActionRail';
import { SessionTimer } from '@/components/SessionTimer';
import { Button } from '@/components/ui/button';
import { getLocalizedStepField } from '@/lib/utils';
import type { PhotoStep } from '@transpordiamet/shared';

interface StepContentProps {
  currentStep: PhotoStep;
  photosCount: number;
  currentStepIndex: number;
  totalSteps: number;
  isOnline: boolean;
  locale: string;
  onCameraClick: () => void;
  onContinue: () => void;
  continueError: string | null;
  photoSaved?: boolean;
}

/**
 * Main content area showing current step details and the capture shutter.
 * The green circular shutter sits in the same 100px right rail as illustration,
 * camera, and confirm so the user can tap one place through the capture flow.
 */
export const StepContent = memo<StepContentProps>(
  ({
    currentStep,
    photosCount,
    currentStepIndex,
    totalSteps,
    isOnline,
    locale,
    onCameraClick,
    onContinue,
    continueError,
    photoSaved = false,
  }) => {
    const stepTitle = getLocalizedStepField(currentStep, 'title', locale);
    const stepDescription = getLocalizedStepField(currentStep, 'description', locale);
    const maxAdditional = currentStep.maxAdditionalPhotos ?? 0;
    const totalAllowed = effectiveMaxPhotos(currentStep);
    const requiredDone = isStepRequiredDone(currentStep, photosCount);
    const canAddMore = canAddMorePhotos(currentStep, photosCount);
    const showContinue = requiredDone;
    const showCamera = canAddMore;

    const cameraLabel = !isOnline
      ? t`Offline - Cannot take photos`
      : requiredDone && maxAdditional > 0 && photosCount > 0
        ? t`TAKE ANOTHER PHOTO (OPTIONAL)`
        : t`TAKE PHOTO`;

    return (
      <div className="bg-white relative flex w-full h-full min-h-0">
        <div
          data-testid="capture-rail-divider"
          className="pointer-events-none absolute inset-y-0 right-[100px] z-10 w-px bg-gray-300"
          aria-hidden
        />

        <div className="flex-1 flex flex-col min-w-0 px-3 py-3">
          <Header />
          <div className="flex-1 flex flex-col mt-4 max-w-2xl w-full">
            {/* Wraps so a long title plus the optional-photo badge cannot push the shutter rail off-screen. */}
            <div className="flex flex-wrap items-center justify-between mb-1.5 gap-x-2 gap-y-1">
              <h1 className="min-w-0 text-xl font-bold text-gray-900 leading-tight [overflow-wrap:anywhere]">
                {stepTitle}
              </h1>
              <div className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                {maxAdditional > 0 ? (
                  <>
                    {Math.min(photosCount, currentStep.photoCount)} / {currentStep.photoCount} {t`required`}
                    {' · '}
                    {Math.max(0, photosCount - currentStep.photoCount)} / {maxAdditional} {t`optional`}
                  </>
                ) : (
                  <>
                    {photosCount} / {currentStep.photoCount} {t`photos`}
                  </>
                )}
              </div>
            </div>

            <div className="mb-3">
              {photoSaved && <p className="text-sm font-medium text-green-700 mb-1">{t`Photo saved`}</p>}
              <p className="text-sm text-gray-700 leading-snug">{stepDescription}</p>
              {maxAdditional > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  {t`Up to ${totalAllowed} photos allowed for this step. Additional photos are optional.`}
                </p>
              )}
            </div>

            {showContinue && showCamera && (
              <Button
                onClick={onContinue}
                className="mb-3 self-start bg-green-600 px-4 font-semibold text-white shadow-sm hover:bg-green-700"
              >
                {t`Continue`}
                <ChevronRight data-icon="inline-end" />
              </Button>
            )}
            {continueError && <p className="mb-3 text-sm text-red-600">{continueError}</p>}

            <SessionTimer />

            <div className="mt-auto pt-2 text-xs text-gray-500">
              <p>{t`Step ${currentStepIndex + 1} / ${totalSteps}`}</p>
            </div>
          </div>
        </div>

        <CaptureActionRail surface="light" className="border-l-0">
          {showCamera && (
            <CaptureShutterButton
              tone="green"
              surface="light"
              onClick={onCameraClick}
              disabled={!isOnline}
              aria-label={cameraLabel}
            >
              <Camera className="w-8 h-8 text-white" />
            </CaptureShutterButton>
          )}
          {!showCamera && showContinue && (
            <CaptureShutterButton tone="blue" surface="light" onClick={onContinue} aria-label={t`Continue`}>
              <ChevronRight className="w-8 h-8 text-white" />
            </CaptureShutterButton>
          )}
        </CaptureActionRail>
      </div>
    );
  },
  (prevProps, nextProps) =>
    prevProps.currentStep.id === nextProps.currentStep.id &&
    prevProps.currentStep.photoCount === nextProps.currentStep.photoCount &&
    prevProps.currentStep.maxAdditionalPhotos === nextProps.currentStep.maxAdditionalPhotos &&
    prevProps.photosCount === nextProps.photosCount &&
    prevProps.currentStepIndex === nextProps.currentStepIndex &&
    prevProps.totalSteps === nextProps.totalSteps &&
    prevProps.isOnline === nextProps.isOnline &&
    prevProps.locale === nextProps.locale &&
    prevProps.onContinue === nextProps.onContinue &&
    prevProps.continueError === nextProps.continueError &&
    prevProps.photoSaved === nextProps.photoSaved
);

StepContent.displayName = 'StepContent';
